import { ethers } from "ethers";

import { getChainLabel, getChainRpcUrl, getWrongNetworkMessage } from "../../config/chainEnv";
import {
  TRDX_CHAIN_ID,
  TRDX_TOKEN_ADDRESS,
  TRDX_SALE_ADDRESS,
  TRDX_USDC_ADDRESS,
  TRDX_ERC20_MINIMAL_ABI,
  TREYDOREX_SALE_ABI,
} from "./trdxContractConfig";

function getTrdxRpcUrl() {
  const fromEnv = String(process.env.REACT_APP_TRDX_RPC_URL || "").trim();
  if (fromEnv) return fromEnv;
  try {
    return getChainRpcUrl(TRDX_CHAIN_ID);
  } catch (err) {
    throw new Error(
      "TRDX read RPC is not configured. Set REACT_APP_TRDX_RPC_URL (recommended) or REACT_APP_MAINNET_RPC_URL."
    );
  }
}

function getJsonRpcProvider() {
  return new ethers.providers.JsonRpcProvider(getTrdxRpcUrl(), TRDX_CHAIN_ID);
}

function assertWalletProvider() {
  if (!window?.ethereum) {
    throw new Error("No wallet provider found. Connect a wallet first.");
  }
}

function getSigner() {
  assertWalletProvider();
  const browserProvider = new ethers.providers.Web3Provider(window.ethereum);
  return browserProvider.getSigner();
}

async function assertConfiguredNetworkSigner() {
  const signer = getSigner();
  const net = await signer.provider.getNetwork();
  if (Number(net.chainId) !== TRDX_CHAIN_ID) {
    throw new Error(getWrongNetworkMessage(TRDX_CHAIN_ID).replace(" to continue.", ` to purchase TRDX.`));
  }
  return signer;
}

function getSaleContractRead(provider) {
  return new ethers.Contract(TRDX_SALE_ADDRESS, TREYDOREX_SALE_ABI, provider);
}

function getTrdxContractRead(provider) {
  return new ethers.Contract(TRDX_TOKEN_ADDRESS, TRDX_ERC20_MINIMAL_ABI, provider);
}

function getUsdcContractRead(provider) {
  return new ethers.Contract(TRDX_USDC_ADDRESS, TRDX_ERC20_MINIMAL_ABI, provider);
}

/** Reads use configured chain RPC (REACT_APP_TRDX_RPC_URL or chain default). */
export function getTrdxReadProvider() {
  return getJsonRpcProvider();
}

export function getTrdxNetworkLabel() {
  return getChainLabel(TRDX_CHAIN_ID);
}

export async function readTrdxBalance(walletAddress) {
  if (!walletAddress) return ethers.constants.Zero;
  const provider = getTrdxReadProvider();
  const trdx = getTrdxContractRead(provider);
  return trdx.balanceOf(walletAddress);
}

/** @param {string} walletAddress @returns {Promise<import("ethers").BigNumber>} Balance in USDC base units (6 decimals). */
export async function readUsdcBalance(walletAddress) {
  if (!walletAddress) return ethers.constants.Zero;
  const provider = getTrdxReadProvider();
  const usdc = getUsdcContractRead(provider);
  return usdc.balanceOf(walletAddress);
}

/** @param {string} walletAddress @returns {Promise<import("ethers").BigNumber>} Allowance for `TRDX_SALE_ADDRESS` (6 decimals). */
export async function readUsdcAllowance(walletAddress) {
  if (!walletAddress) return ethers.constants.Zero;
  const provider = getTrdxReadProvider();
  const usdc = getUsdcContractRead(provider);
  return usdc.allowance(walletAddress, TRDX_SALE_ADDRESS);
}

export async function readSaleInventory() {
  const provider = getTrdxReadProvider();
  const trdx = getTrdxContractRead(provider);
  return trdx.balanceOf(TRDX_SALE_ADDRESS);
}

export async function readTokensPerEth() {
  const provider = getTrdxReadProvider();
  const sale = getSaleContractRead(provider);
  return sale.tokensPerEth();
}

export async function readPricePerTrdxUsdc() {
  const provider = getTrdxReadProvider();
  const sale = getSaleContractRead(provider);
  return sale.pricePerTrdxUsdc();
}

export async function readPaused() {
  const provider = getTrdxReadProvider();
  const sale = getSaleContractRead(provider);
  return sale.paused();
}

export async function quoteEthToTrdx(ethAmountWei) {
  if (!ethAmountWei || ethAmountWei.isZero()) return ethers.constants.Zero;
  const provider = getTrdxReadProvider();
  const sale = getSaleContractRead(provider);
  return sale.quoteETH(ethAmountWei);
}

/**
 * Inverse of quoteETH: ETH wei required to receive at least `trdxAmountWei` TRDX (rounded up).
 * @param {import("ethers").BigNumber} trdxAmountWei
 * @param {import("ethers").BigNumber} [tokensPerEthWei] optional cached rate
 */
export async function quoteTrdxToEth(trdxAmountWei, tokensPerEthWei) {
  if (!trdxAmountWei || trdxAmountWei.isZero()) return ethers.constants.Zero;
  const rate = tokensPerEthWei || (await readTokensPerEth());
  if (!rate || rate.isZero()) return ethers.constants.Zero;
  return trdxAmountWei.mul(ethers.constants.WeiPerEther).add(rate.sub(1)).div(rate);
}

export async function readEthBalance(walletAddress) {
  if (!walletAddress) return ethers.constants.Zero;
  assertWalletProvider();
  const browserProvider = new ethers.providers.Web3Provider(window.ethereum);
  return browserProvider.getBalance(walletAddress);
}

/** @param {import("ethers").BigNumber} usdcAmountUnits USDC amount in 6 decimals. */
export async function quoteUsdcToTrdx(usdcAmountUnits) {
  if (!usdcAmountUnits || usdcAmountUnits.isZero()) return ethers.constants.Zero;
  const provider = getTrdxReadProvider();
  const sale = getSaleContractRead(provider);
  return sale.quoteUSDC(usdcAmountUnits);
}

/** @param {import("ethers").BigNumber} usdcAmount Exact USDC amount (6 decimals) to approve for the sale contract. */
export async function approveUsdcForTrdxSale(usdcAmount) {
  const signer = await assertConfiguredNetworkSigner();
  const usdc = new ethers.Contract(TRDX_USDC_ADDRESS, TRDX_ERC20_MINIMAL_ABI, signer);
  const tx = await usdc.approve(TRDX_SALE_ADDRESS, usdcAmount);
  const receipt = await tx.wait();
  return { txHash: tx.hash, receipt };
}

/** Submits buyWithETH and returns immediately with tx hash (caller awaits `wait()`). */
export async function submitBuyTrdxWithEth(ethAmountWei, minTrdxOut) {
  const signer = await assertConfiguredNetworkSigner();
  const sale = new ethers.Contract(TRDX_SALE_ADDRESS, TREYDOREX_SALE_ABI, signer);
  const tx = await sale.buyWithETH(minTrdxOut, { value: ethAmountWei });
  return {
    txHash: tx.hash,
    wait: () => tx.wait(),
  };
}

export async function buyTrdxWithEth(ethAmountWei, minTrdxOut) {
  const submitted = await submitBuyTrdxWithEth(ethAmountWei, minTrdxOut);
  const receipt = await submitted.wait();
  return { txHash: submitted.txHash, receipt };
}

/** @param {import("ethers").BigNumber} usdcAmount USDC to spend (6 decimals). @param {import("ethers").BigNumber} minTrdxOut */
export async function buyTrdxWithUsdc(usdcAmount, minTrdxOut) {
  const signer = await assertConfiguredNetworkSigner();
  const sale = new ethers.Contract(TRDX_SALE_ADDRESS, TREYDOREX_SALE_ABI, signer);
  const tx = await sale.buyWithUSDC(usdcAmount, minTrdxOut);
  const receipt = await tx.wait();
  return { txHash: tx.hash, receipt };
}
